import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { LogOut, Save, Loader2, DollarSign, Package } from 'lucide-react';
import { listProductPrices, setProductPrice, getToken, clearToken, me } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo, apparelProducts } from '../mock';

export default function AdminProducts() {
  const { toast } = useToast();
  const [authOk, setAuthOk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricesById, setPricesById] = useState({});
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!getToken()) { setAuthOk(false); return; }
    me().then(() => setAuthOk(true)).catch(() => { clearToken(); setAuthOk(false); });
  }, []);

  useEffect(() => {
    if (!authOk) return;
    (async () => {
      try {
        const overrides = await listProductPrices();
        const map = {};
        for (const p of apparelProducts) {
          map[p.id] = overrides[p.id] != null ? overrides[p.id] : (p.price ?? '');
        }
        setPricesById(map);
      } catch (e) {
        toast({ title: 'Error', description: e.response?.data?.detail || e.message });
      } finally { setLoading(false); }
    })();
  }, [authOk, toast]);

  const setPrice = (id, val) => setPricesById((prev) => ({ ...prev, [id]: val }));

  const save = async (id) => {
    const val = parseFloat(pricesById[id]);
    if (isNaN(val) || val < 0) {
      toast({ title: 'Enter a valid price' });
      return;
    }
    setBusyId(id);
    try {
      await setProductPrice(id, val);
      toast({ title: 'Price saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally { setBusyId(null); }
  };

  const bulkSetCategory = async (category, price) => {
    if (!window.confirm(`Set every ${category} item to $${price}?`)) return;
    const items = apparelProducts.filter((p) => p.category === category);
    for (const p of items) {
      await setProductPrice(p.id, price);
      setPricesById((prev) => ({ ...prev, [p.id]: price }));
    }
    toast({ title: `${items.length} ${category} items → $${price}` });
  };

  if (authOk === null) return <div className="min-h-screen bg-[#0a1f44] flex items-center justify-center text-white">Loading…</div>;
  if (authOk === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#f5f6f8]">
      <header className="bg-[#0a1f44] text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={iceLogo} alt="ICE" className="h-10 w-auto" />
            <div>
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Admin Console</div>
              <div className="font-display text-2xl leading-none mt-1">PRICES</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/scholars" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Scholars</Link>
            <Link to="/admin/orders" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Orders</Link>
            <Link to="/admin/inventory" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Inventory</Link>
            <Link to="/admin/season" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Season</Link>
            <Link to="/admin/account" data-testid="admin-nav-account" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Change Password</Link>
            <button
              onClick={() => { clearToken(); window.location.href = '/admin/login'; }}
              className="inline-flex items-center gap-1 text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20"
            >
              <LogOut className="w-3 h-3" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-white border border-[#0a1f44]/10 p-6 mb-6">
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px] mb-2">Bulk Actions</div>
          <div className="flex flex-wrap gap-2 text-sm">
            <button onClick={() => bulkSetCategory('Headwear', 15)} data-testid="bulk-headwear-15" className="bg-[#0a1f44] hover:bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 transition-colors">Set All Headwear → $15</button>
            <button onClick={() => bulkSetCategory('Apparel', 25)} className="bg-[#0a1f44] hover:bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 transition-colors">Set All Apparel → $25</button>
            <button onClick={() => bulkSetCategory('Fan Gear', 20)} className="bg-[#0a1f44] hover:bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 transition-colors">Set All Fan Gear → $20</button>
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center text-[#0a1f44]/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="bg-white border border-[#0a1f44]/10 overflow-hidden">
            <table className="mobile-stack-table w-full text-sm" data-testid="products-price-table">
              <thead className="bg-[#0a1f44] text-white">
                <tr>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Photo</th>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Product</th>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Category</th>
                  <th className="px-3 py-3 text-right font-heading uppercase tracking-widest text-[10px]">Price ($)</th>
                  <th className="px-3 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Save</th>
                </tr>
              </thead>
              <tbody>
                {apparelProducts.map((p) => (
                  <tr key={p.id} data-testid={`price-row-${p.id}`} className="border-t border-[#0a1f44]/5 hover:bg-[#0a1f44]/[0.02]">
                    <td data-label="Photo" className="px-3 py-2">
                      <img src={p.img} alt={p.name} className="w-14 h-14 object-cover" loading="lazy" decoding="async" />
                    </td>
                    <td data-label="Product" className="px-3 py-2">
                      <div className="font-heading text-[#0a1f44]">{p.name}</div>
                      <div className="text-xs text-[#0a1f44]/50">{p.tagline}</div>
                    </td>
                    <td data-label="Category" className="px-3 py-2 text-xs font-heading uppercase tracking-widest text-[#0a1f44]/60">{p.category}</td>
                    <td data-label="Price" className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1 border border-[#0a1f44]/20 px-2 py-1 focus-within:border-[#c8102e]">
                        <DollarSign className="w-3 h-3 text-[#0a1f44]/40" />
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={pricesById[p.id] ?? ''}
                          onChange={(e) => setPrice(p.id, e.target.value)}
                          data-testid={`price-input-${p.id}`}
                          placeholder="TBA"
                          className="w-20 text-right font-display text-lg focus:outline-none"
                        />
                      </div>
                    </td>
                    <td data-label="Save" className="px-3 py-2 text-center">
                      <button
                        onClick={() => save(p.id)}
                        disabled={busyId === p.id}
                        data-testid={`price-save-${p.id}`}
                        className="inline-flex items-center gap-1 bg-[#c8102e] hover:bg-[#e11d34] disabled:opacity-50 text-white font-heading uppercase tracking-widest text-[9px] px-3 py-1.5 transition-colors"
                      >
                        {busyId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Save
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-4 text-xs text-[#0a1f44]/50 font-heading uppercase tracking-widest">
          <Package className="w-3 h-3 inline mr-1" /> Prices update live on the storefront the moment you hit Save — no rebuild required.
        </p>
      </section>
    </div>
  );
}
